// Ведущий протокола (014: T029, T030; FR-032, FR-033; research, Р6). Ведущим можно назначить
// любого действующего пользователя: сервер выдаёт роль «Ведущий» с областью этого протокола.
// Кандидаты — minutes.leads.candidates (право documents.create): только номер и ФИО, поиск от двух
// знаков на каждое нажатие с короткой задержкой. Пусто — «Никого не нашлось», ответ дольше 0,3 с —
// «Поиск…», сбой — причина, код, номер операции и «Повторить» (README «Табеля», «Состояния»).
//
// LeadSearchField — поле «Ведущий» окна «Протокол в системе» (по желанию). LeadValue — свойство
// «Ведущий» в карточке протокола: пилюля с выбором, без «Сохранить» (ux.md, разделы 2 и 5);
// смена прежнего ведущего — окно подтверждения «У прежнего ведущего пропадёт доступ к протоколу»
// (план 014, «Решения после анализа»), первое назначение — сразу. Менять ведущего может тот, кто
// создаёт протоколы (documents.create), у протокола в режиме ведения; остальным — только имя.
//
// 023 (spec, сценарий 7; FR-009; research Р5 п. 5): протокол без ведущего получает ведущим создателя,
// если ему можно быть ведущим (кандидат в ведущие — без роли «Сопровождение», сотрудник компании).
// Пока ведущий не выбран, под полем окна — «Не выбран — ведущим станете вы» (canLeadSelf).
import { LOOKUP_QUERY_MAX, LOOKUP_QUERY_MIN } from '@kp/contracts';
import type { DocumentRow, MeResponse } from '@kp/contracts';
import {
  ConfirmDialog,
  Icon,
  Popover,
  PropPill,
  SearchField,
  SearchPickerField,
  useDelayedFlag,
  useToast,
} from '@kp/ui';
import type { SearchPickerOption } from '@kp/ui';
import { Check, UserRound, UserRoundCog } from '@kp/ui/icons';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, Ref } from 'react';
import { api, apiQuery } from '../../api/client.ts';
import { useErrorToast } from '../../app/errors.ts';
import type { ErrorView } from '../../app/errors.ts';
import { can } from '../../shell/permissions.ts';
import { useMe } from '../../shell/session.ts';
import { ListLoadError, failedListText, useQueryFailure } from '../load-error.tsx';

const SEARCH_DELAY_MS = 250;

export const LEAD_CANDIDATES_FAILED = 'Не удалось загрузить список пользователей';
/** Последствие смены ведущего — в окне подтверждения (необратимо для другого человека). */
export const LEAD_CHANGE_CONSEQUENCE = 'У прежнего ведущего пропадёт доступ к протоколу';
const TYPE_MORE = 'Введите ФИО — от двух знаков';
const NOBODY = 'Никого не нашлось. Проверьте раскладку';
/** Вариант «без ведущего» в поле окна: выбранного можно снять. */
const NO_LEAD = '';
export const LEAD_HINT =
  'Получит роль «Ведущий» с доступом к этому протоколу. Можно назначить и позже';
/** Ведущий не выбран, а создателю можно быть ведущим: сервер назначит его (023, research Р5). */
export const SELF_LEAD_HINT = 'Не выбран — ведущим станете вы';

/**
 * Создатель станет ведущим протокола без ведущего: как кандидат в ведущие на сервере
 * (leadCandidateUsers) — без роли «Сопровождение» и не внешний. Архив и блокировку проверять не
 * нужно: такой пользователь не входит в систему.
 */
export function canLeadSelf(me: Pick<MeResponse, 'roles' | 'external'>): boolean {
  return !me.external && !me.roles.some((item) => item.role === 'support');
}

export interface LeadCandidate {
  readonly id: string;
  readonly name: string;
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface LeadCandidates {
  readonly items: readonly LeadCandidate[];
  /** Введено от двух знаков: поиск идёт или пришёл. */
  readonly ready: boolean;
  /** Ответа на введённое ещё нет. */
  readonly loading: boolean;
  /** Ответ пришёл и пуст. */
  readonly nobody: boolean;
  readonly failure: ErrorView | null;
  readonly retrying: boolean;
  readonly retry: () => void;
}

/** Кандидаты в ведущие по строке поиска (minutes.leads.candidates, до 20). */
export function useLeadCandidates(text: string, enabled = true): LeadCandidates {
  const typed = text.trim().slice(0, LOOKUP_QUERY_MAX);
  const q = useDebounced(typed, SEARCH_DELAY_MS);
  const ready = typed.length >= LOOKUP_QUERY_MIN;
  const candidates = useQuery({
    ...apiQuery('minutes.leads.candidates', { query: { q } }),
    enabled: enabled && q.length >= LOOKUP_QUERY_MIN,
    placeholderData: keepPreviousData,
  });
  const { failure, retrying, retry } = useQueryFailure(candidates, { show: ready });
  const items = ready && !failure ? (candidates.data?.items ?? []) : [];
  const answered = q === typed && candidates.isSuccess && !candidates.isFetching;
  return {
    items,
    ready,
    loading: ready && !failure && !answered && items.length === 0,
    nobody: ready && !failure && answered && items.length === 0,
    failure,
    retrying,
    retry,
  };
}

export interface LeadSearchFieldProps {
  value: SearchPickerOption | null;
  onChange: (option: SearchPickerOption | null) => void;
  error?: string;
  /** Подсказка под полем; по умолчанию — что получит ведущий (LEAD_HINT). */
  hint?: string | undefined;
  ref?: Ref<HTMLButtonElement>;
}

/** «Ведущий» окна «Протокол в системе» — по желанию (FR-032). */
export function LeadSearchField({
  value,
  onChange,
  error,
  hint = LEAD_HINT,
  ref,
}: LeadSearchFieldProps) {
  const [query, setQuery] = useState('');
  const candidates = useLeadCandidates(query);
  const { failure } = candidates;
  const options: SearchPickerOption[] = [
    ...(value ? [{ value: NO_LEAD, label: 'Без ведущего' }] : []),
    ...candidates.items.map((candidate) => ({ value: candidate.id, label: candidate.name })),
  ];
  return (
    <>
      <SearchPickerField
        ref={ref}
        label="Ведущий"
        optional
        hint={hint}
        placeholder="Не назначен"
        searchPlaceholder="Найти по ФИО"
        emptyText={
          !candidates.ready
            ? TYPE_MORE
            : failure
              ? failedListText(LEAD_CANDIDATES_FAILED, failure)
              : NOBODY
        }
        value={value}
        query={query}
        onQueryChange={setQuery}
        options={options}
        loading={candidates.loading}
        onChange={(option) => onChange(option.value === NO_LEAD ? null : option)}
        error={error}
      />
      {failure ? (
        <ListLoadError
          what={LEAD_CANDIDATES_FAILED}
          view={failure}
          retrying={candidates.retrying}
          onRetry={candidates.retry}
        />
      ) : null}
    </>
  );
}

/** ↑ ↓ Home End — по вариантам панели (ux.md, раздел 5); из поля поиска ↓ — на первый. */
function moveFocus(event: KeyboardEvent<HTMLElement>, list: HTMLElement | null): void {
  const options = Array.from(list?.querySelectorAll<HTMLButtonElement>('[data-option]') ?? []);
  if (options.length === 0) return;
  if (event.key === 'ArrowDown' && event.target instanceof HTMLInputElement) {
    event.preventDefault();
    options[0]?.focus();
    return;
  }
  const index = options.indexOf(document.activeElement as HTMLButtonElement);
  const target =
    event.key === 'ArrowDown'
      ? options[Math.min(index + 1, options.length - 1)]
      : event.key === 'ArrowUp'
        ? options[Math.max(index - 1, 0)]
        : event.key === 'Home'
          ? options[0]
          : event.key === 'End'
            ? options.at(-1)
            : undefined;
  if (!target) return;
  event.preventDefault();
  target.focus();
}

/** Смена ведущего: тост с итогом, сбой — тост с кодом и «Повторить», ошибка — дальше. */
function useSetLead(row: DocumentRow): (candidate: LeadCandidate) => Promise<void> {
  const queryClient = useQueryClient();
  const toast = useToast();
  const showError = useErrorToast();
  const set = async (candidate: LeadCandidate): Promise<void> => {
    try {
      const result = await api('minutes.lead.set', {
        params: { id: row.id },
        body: { userId: candidate.id },
      });
      void queryClient.invalidateQueries({ queryKey: ['api', 'documents.list'] });
      toast.ok(`Ведущий П-${row.number}: ${result.lead?.name ?? candidate.name}`);
    } catch (error) {
      showError(error, () => void set(candidate).catch(() => undefined));
      throw error;
    }
  };
  return set;
}

/** «Ведущий» в карточке протокола (FR-033). */
export function LeadValue({ row }: { row: DocumentRow }) {
  const me = useMe();
  const editable = row.mode === 'maintained' && can(me.permissions, ['documents.create']);
  if (editable) return <LeadPicker row={row} />;
  return row.lead ? <span>{row.lead.name}</span> : <span className="t-muted">Не назначен</span>;
}

function LeadPicker({ row }: { row: DocumentRow }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  /** Выбран другой человек — ждёт подтверждения смены. */
  const [next, setNext] = useState<LeadCandidate | null>(null);
  const pillRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const candidates = useLeadCandidates(text, open);
  const searching = useDelayedFlag(candidates.loading);
  const setLead = useSetLead(row);
  const current = row.lead;

  const choose = (candidate: LeadCandidate) => {
    setOpen(false);
    setText('');
    if (candidate.id === current?.userId) return;
    // Окно подтверждения — после того, как панель вернула фокус на пилюлю: окно запомнит её.
    if (current) window.setTimeout(() => setNext(candidate));
    else void setLead(candidate).catch(() => undefined);
  };

  return (
    <>
      <Popover
        label="Ведущий"
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) setText('');
        }}
        className="kp-lead-pop"
        trigger={
          <PropPill
            empty={!current}
            aria-label={current ? `Изменить ведущего: ${current.name}` : 'Назначить ведущего'}
            onFocus={(event) => {
              pillRef.current = event.currentTarget;
            }}
          >
            {current ? current.name : 'Назначить ведущего'}
          </PropPill>
        }
      >
        <div className="t-col" onKeyDown={(event) => moveFocus(event, listRef.current)}>
          <SearchField
            label="Поиск пользователя по ФИО"
            placeholder="Найти по ФИО"
            maxLength={LOOKUP_QUERY_MAX}
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <ul ref={listRef} className="kp-lead-options" aria-label="Кандидаты в ведущие">
            {candidates.items.map((candidate) => {
              const chosen = candidate.id === current?.userId;
              return (
                <li key={candidate.id}>
                  <button
                    type="button"
                    data-option=""
                    className="kp-lead-option"
                    aria-current={chosen ? 'true' : undefined}
                    onClick={() => choose(candidate)}
                  >
                    <Icon icon={UserRound} />
                    <span className="kp-grow">{candidate.name}</span>
                    {chosen ? <Icon icon={Check} /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {candidates.failure ? (
            <ListLoadError
              what={LEAD_CANDIDATES_FAILED}
              view={candidates.failure}
              retrying={candidates.retrying}
              onRetry={candidates.retry}
            />
          ) : !candidates.ready ? (
            <p className="t-muted kp-lead-hint">{TYPE_MORE}</p>
          ) : searching ? (
            <p className="t-muted kp-lead-hint">Поиск…</p>
          ) : candidates.nobody ? (
            <p className="t-muted kp-lead-hint">{NOBODY}</p>
          ) : null}
        </div>
      </Popover>
      <ConfirmDialog
        open={next !== null}
        onOpenChange={(value) => {
          if (!value) setNext(null);
        }}
        tone="warn"
        icon={UserRoundCog}
        title={`Сменить ведущего П-${row.number}?`}
        description={
          next && current
            ? `${LEAD_CHANGE_CONSEQUENCE}: ${current.name} больше не сможет его вести. Ведущим станет ${next.name}`
            : LEAD_CHANGE_CONSEQUENCE
        }
        confirmLabel="Сменить ведущего"
        onConfirm={() => (next ? setLead(next) : undefined)}
        returnFocusRef={pillRef}
      />
    </>
  );
}
